import { Switch, Route, Router, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AppShell } from "@/components/AppShell";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/Dashboard";
import ContractDetail from "@/pages/ContractDetail";
import Sign from "@/pages/Sign";
import SignComplete from "@/pages/SignComplete";
import SignRepay from "@/pages/SignRepay";
import Reconciliation from "@/pages/Reconciliation";
import Settings from "@/pages/Settings";

// VITE_CUSTOMER_ONLY=1 builds a customer-facing-only bundle (used for the
// public esign.sunrental.com Pages site). When set, everything outside the
// /sign/* signing flow shows a generic 'invalid link' page instead of
// exposing the owner dashboard.
const CUSTOMER_ONLY = ((import.meta as any).env?.VITE_CUSTOMER_ONLY as string | undefined) === "1";

function CustomerOnlyLanding() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-6">
      <div className="max-w-md text-center space-y-3">
        <div className="text-2xl font-semibold">Sun Rental Contracts</div>
        <p className="text-muted-foreground">
          This page is for signing rental contracts. If you arrived here without
          a signing link, please use the link your sales rep sent you.
        </p>
      </div>
    </div>
  );
}

// Public signing flow has no AppShell — it's the client-facing surface.
function AppRouter() {
  const [location] = useLocation();
  const isPublic = location.startsWith("/sign/");

  if (isPublic) {
    return (
      <Switch>
        <Route path="/sign/:token/complete" component={SignComplete} />
        <Route path="/sign/:token/repay" component={SignRepay} />
        <Route path="/sign/:token" component={Sign} />
        <Route component={NotFound} />
      </Switch>
    );
  }

  if (CUSTOMER_ONLY) {
    return <CustomerOnlyLanding />;
  }

  return (
    <AppShell>
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/reconciliation" component={Reconciliation} />
        <Route path="/settings" component={Settings} />
        <Route path="/contract/:id" component={ContractDetail} />
        <Route component={NotFound} />
      </Switch>
    </AppShell>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Router hook={useHashLocation}>
            <AppRouter />
          </Router>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
